import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import toast from 'react-hot-toast';
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import { useDispatch } from 'store';
import {
  deleteEvalmetrics,
  downloadForeMatricCSV,
  getBackTest,
  getConfig,
  getEvalMetrics,
  postEvalMetrics,
  putEvalmetrics,
  uploadCSV,
} from 'store/slices/evalmetrics';
import { getSubCategoriesListByCategory } from 'store/slices/categories';
import LightButton from 'ui-component/button/LightButton';
import PrimaryButton from 'ui-component/button/PrimaryButton';
import AnimateButton from 'ui-component/extended/AnimateButton';
import Loader from 'ui-component/Loader';
import DropzoneContainer from './DropzoneContainer';
import BackTests from './BackTests';

// Validation
const validateMetric = (metric) => {
  const errors = {};

  if (!metric.metric || metric.metric.trim() === '') {
    errors.metric = 'Metric is required';
  }

  if (metric.mqe === '' || metric.mqe === undefined) {
    errors.mqe = 'MQE Strategy is required';
  }

  if (metric.hfri === '' || metric.hfri === undefined) {
    errors.hfri = 'HFRI 500 is required';
  }

  return errors;
};

const getBacktestDateLabel = (row) => {
  const value = row?.date || row?.['Unnamed: 0'];
  const text = String(value ?? '').trim();
  return text || 'N/A';
};

const MainBackTests = () => {
  const theme = useTheme();
  const { categoryId, subCategoryId } = useParams();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // Computed values
  const assetId = useMemo(
    () => subCategoryId || categoryId,
    [subCategoryId, categoryId]
  );

  // State
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [isSubCategoryLoaded, setIsSubCategoryLoaded] = useState(false);
  const [displayConfig, setDisplayConfig] = useState({
    index1: 'MQE',
    index2: 'S&P 500',
    index3: 'HFRI 500',
  });

  // Fetch subcategory details
  useEffect(() => {
    setIsSubCategoryLoaded(false);
    const payload = {
      categoryId,
      skip: 0,
      limit: 100,
      type: subCategoryId ? 'assets' : 'portfolio',
    };

    dispatch(getSubCategoriesListByCategory(payload))
      .then((res) => {
        if (res?.status === 200) {
          const data = res.data?.data || res.data;
          const selectedItem =
            payload.type === 'assets'
              ? data.find(
                  (item) =>
                    item._id === subCategoryId || item.id === subCategoryId
                )
              : data.find(
                  (item) => item._id === categoryId || item.id === categoryId
                );
          setSelectedSubCategory(selectedItem);
          setIsSubCategoryLoaded(true);
        }
      })
      .catch(() => {
        setIsSubCategoryLoaded(true);
      });
  }, [dispatch, subCategoryId, categoryId]);

  // Fetch display config for backtest table headers
  useEffect(() => {
    const scope = subCategoryId ? 'asset' : 'portfolio';
    dispatch(getConfig({ scope, assetId }))
      .then((res) => {
        if (res?.status === 200 && res?.data) {
          setDisplayConfig((prev) => ({ ...prev, ...res.data }));
        }
      })
      .catch(() => {
        // Silent fail - use default config
      });
  }, [dispatch, subCategoryId, assetId]);

  // Query keys
  const metricsQueryKey = useMemo(() => ['metrics', assetId], [assetId]);
  const backtestsQueryKey = useMemo(() => ['backtests', assetId], [assetId]);

  // Fetch metrics with React Query
  const {
    data: fetchedMetrics = [],
    isError: isLoadingMetricsError,
    isFetching: isFetchingMetrics,
    isLoading: isLoadingMetrics,
  } = useQuery({
    queryKey: metricsQueryKey,
    queryFn: async () => {
      const response = await dispatch(
        getEvalMetrics({
          assetId,
          limit: 1000,
          skip: 0,
          type: subCategoryId ? 'assets' : 'portfolio',
        })
      );
      return response?.data || [];
    },
    enabled: isSubCategoryLoaded, // Only run after subcategory is loaded
    refetchOnWindowFocus: false,
    onError: () => {
      toast.error('Failed to fetch metrics');
    },
  });

  // Fetch backtests with React Query (for the table)
  // Only fetch after subcategory is loaded
  const {
    data: fetchedBacktests = [],
    isError: isLoadingBacktestsError,
    isFetching: isFetchingBacktests,
    isLoading: isLoadingBacktests,
  } = useQuery({
    queryKey: backtestsQueryKey,
    queryFn: async () => {
      const response = await dispatch(
        getBackTest({
          assetId,
          limit: 1000,
          skip: 0,
          type: subCategoryId ? 'assets' : 'portfolio',
        })
      );
      return response?.data || [];
    },
    enabled: isSubCategoryLoaded, // Only run after subcategory is loaded
    refetchOnWindowFocus: false,
    onError: () => {
      toast.error('Failed to fetch backtests');
    },
  });

  // Mutations for metrics
  const createMetric = useMutation({
    mutationFn: async (metric) => {
      const payload = {
        ...metric,
        asset_id: assetId,
        mqe: parseFloat(metric.mqe),
        hfri: parseFloat(metric.hfri),
        hfrititle: 'hfri',
      };
      const response = await dispatch(postEvalMetrics(payload));
      if (response.status !== 200) {
        throw new Error(response?.detail || 'Failed to add metric');
      }
      return response;
    },
    onSuccess: () => {
      toast.success('Metric added successfully');
      // Refetch both metrics and backtests
      queryClient.invalidateQueries(metricsQueryKey);
      queryClient.invalidateQueries(backtestsQueryKey);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to add metric');
    },
  });

  const updateMetric = useMutation({
    mutationFn: async (metric) => {
      const payload = {
        ...metric,
        asset_id: assetId,
        mqe: parseFloat(metric.mqe),
        hfri: parseFloat(metric.hfri),
        _id: selectedRow.original._id,
      };
      const response = await dispatch(putEvalmetrics(payload));
      if (response.status !== 200) {
        throw new Error(response?.detail || 'Failed to update metric');
      }
      return response;
    },
    onSuccess: () => {
      toast.success('Metric updated successfully');
      // Refetch both metrics and backtests
      queryClient.invalidateQueries(metricsQueryKey);
      queryClient.invalidateQueries(backtestsQueryKey);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update metric');
    },
  });

  const deleteMetric = useMutation({
    mutationFn: async (metricId) => {
      await dispatch(deleteEvalmetrics({ _id: metricId }));
    },
    onSuccess: () => {
      toast.success('Metric deleted successfully');
      // Refetch both metrics and backtests
      queryClient.invalidateQueries(metricsQueryKey);
      queryClient.invalidateQueries(backtestsQueryKey);
    },
    onError: () => {
      toast.error('Failed to delete metric');
    },
  });

  // Handlers
  const handleCreateMetric = useCallback(
    async ({ values, table }) => {
      const newValidationErrors = validateMetric(values);
      if (Object.values(newValidationErrors).some((error) => error)) {
        setValidationErrors(newValidationErrors);
        return;
      }
      setValidationErrors({});
      await createMetric.mutateAsync(values);
      table.setCreatingRow(null);
    },
    [createMetric]
  );

  const handleSaveMetric = useCallback(
    async ({ values, table }) => {
      const newValidationErrors = validateMetric(values);
      if (Object.values(newValidationErrors).some((error) => error)) {
        setValidationErrors(newValidationErrors);
        return;
      }
      setValidationErrors({});
      await updateMetric.mutateAsync(values);
      table.setEditingRow(null);
    },
    [updateMetric]
  );

  const handleDownloadCSV = useCallback(async () => {
    try {
      const type = subCategoryId ? 'assets' : 'portfolios';
      const response = await dispatch(downloadForeMatricCSV(assetId, type));

      const csvData = response.data;
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `metrics_${assetId}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Metrics downloaded successfully');
    } catch (err) {
      toast.error('Error downloading file');
    }
  }, [assetId, subCategoryId, dispatch]);

  const handleUploadCSV = useCallback(async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await dispatch(uploadCSV(assetId, formData));

      if (response?.data?.message === 'Invalid CSV Format') {
        toast.error('Invalid CSV Format');
      } else {
        toast.success('CSV uploaded successfully');
        // Refetch both metrics and backtests
        queryClient.invalidateQueries(metricsQueryKey);
        queryClient.invalidateQueries(backtestsQueryKey);
        setFile(null);
        setOpenUploadDialog(false);
      }
    } catch (error) {
      toast.error('Error uploading file');
    }
  }, [
    file,
    assetId,
    dispatch,
    queryClient,
    metricsQueryKey,
    backtestsQueryKey,
  ]);

  const openDeleteConfirmModal = useCallback((row) => {
    setSelectedRow(row);
    setOpenDialog(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setOpenDialog(false);
    setSelectedRow(null);
  }, []);

  const handleDialogConfirm = useCallback(async () => {
    if (selectedRow) {
      await deleteMetric.mutateAsync(selectedRow.original._id);
      setOpenDialog(false);
      setSelectedRow(null);
    }
  }, [selectedRow, deleteMetric]);

  // Table columns for metrics
  const metricsColumns = useMemo(
    () => [
      {
        accessorKey: 'metric',
        header: 'Metric',
        muiEditTextFieldProps: {
          type: 'text',
          required: true,
          error: !!validationErrors?.metric,
          helperText: validationErrors?.metric,
          onFocus: () =>
            setValidationErrors((prev) => ({
              ...prev,
              metric: undefined,
            })),
        },
      },
      {
        accessorKey: 'mqe',
        header: 'MQE Strategy',
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return typeof value === 'number' ? value.toFixed(2) : value || '0.00';
        },
        muiEditTextFieldProps: {
          type: 'number',
          required: true,
          error: !!validationErrors?.mqe,
          helperText: validationErrors?.mqe,
          onFocus: () =>
            setValidationErrors((prev) => ({
              ...prev,
              mqe: undefined,
            })),
        },
      },
      {
        accessorKey: 'hfri',
        header: 'HFRI 500',
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return typeof value === 'number' ? value.toFixed(2) : value || '0.00';
        },
        muiEditTextFieldProps: {
          type: 'number',
          required: true,
          error: !!validationErrors?.hfri,
          helperText: validationErrors?.hfri,
          onFocus: () =>
            setValidationErrors((prev) => ({
              ...prev,
              hfri: undefined,
            })),
        },
      },
    ],
    [validationErrors]
  );

  // Table columns for backtests
  const backtestsColumns = useMemo(
    () => [
      {
        accessorFn: getBacktestDateLabel,
        header: 'Date',
        Cell: ({ cell }) => cell.getValue(),
      },
      {
        accessorFn: (row) => row.index1 || row.mqe,
        header: displayConfig.index1 || 'MQE',
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return typeof value === 'number' ? value.toFixed(2) : value || '0.00';
        },
      },
      {
        accessorFn: (row) => row.index2 || row.sp500,
        header: displayConfig.index2 || 'S&P 500',
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return typeof value === 'number' ? value.toFixed(2) : value || '0.00';
        },
      },
      {
        accessorFn: (row) => row.index3 || row.hari500 || row.exchange_rate,
        header: displayConfig.index3 || 'HFRI 500',
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return typeof value === 'number' ? value.toFixed(2) : value || '0.00';
        },
      },
    ],
    [displayConfig]
  );

  // Table configuration for metrics
  const metricsTable = useMaterialReactTable({
    columns: metricsColumns,
    data: fetchedMetrics,
    createDisplayMode: 'row',
    editDisplayMode: 'row',
    enableEditing: true,
    positionActionsColumn: 'last',
    getRowId: (row) => row._id || row.id,
    muiToolbarAlertBannerProps: isLoadingMetricsError
      ? {
          color: 'error',
          children: 'Error loading data',
        }
      : undefined,
    muiTableContainerProps: {
      sx: {
        minHeight: '500px',
      },
    },
    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateMetric,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleSaveMetric,
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: 'flex', gap: '1rem' }}>
        <Tooltip title="Edit">
          <IconButton
            onClick={() => {
              setSelectedRow(row);
              table.setEditingRow(row);
            }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <AnimateButton>
        <LightButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            table.setCreatingRow(true);
          }}
          title="Add Metric"
          style={{
            padding: '8px 20px',
          }}
        />
      </AnimateButton>
    ),
    state: {
      isLoading: isLoadingMetrics,
      isSaving:
        createMetric.isPending ||
        updateMetric.isPending ||
        deleteMetric.isPending,
      showAlertBanner: isLoadingMetricsError,
      showProgressBars: isFetchingMetrics,
    },
  });

  // Table configuration for backtests
  const backtestsTable = useMaterialReactTable({
    columns: backtestsColumns,
    data: fetchedBacktests,
    enableEditing: false,
    positionActionsColumn: 'last',
    getRowId: (row) => row._id || row.id,
    muiToolbarAlertBannerProps: isLoadingBacktestsError
      ? {
          color: 'error',
          children: 'Error loading backtest data',
        }
      : undefined,
    muiSearchTextFieldProps: {
      sx: {
        marginBottom: '16px',
        minWidth: '300px',
        maxWidth: '400px',
      },
    },
    muiTopToolbarProps: {
      sx: {
        marginBottom: 2,
        flexWrap: 'wrap',
        gap: 2,
        overflow: 'visible',
      },
    },
    muiTableContainerProps: {
      sx: {
        minHeight: '500px',
      },
    },
    state: {
      isLoading: isLoadingBacktests,
      showAlertBanner: isLoadingBacktestsError,
      showProgressBars: isFetchingBacktests,
    },
  });

  if (isLoadingMetrics || isLoadingBacktests) {
    return <Loader />;
  }

  return (
    <Grid container spacing={3}>
      {/* BackTests Component with Chart - it will auto-update when data changes */}
      <BackTests selectedSubCategory={selectedSubCategory} />

      {/* Backtests Table Section */}
      <Grid item xs={12} mt={4}>
        <Typography variant="h6" fontWeight={500} mb={2}>
          Backtest Data
          {selectedSubCategory?.name && ` - ${selectedSubCategory.name}`}
        </Typography>
      </Grid>

      {/* Backtests Table - auto-updates when data changes */}
      <Grid item xs={12}>
        <Box sx={{ overflow: 'visible', width: '100%' }}>
          <MaterialReactTable table={backtestsTable} />
        </Box>
      </Grid>

      {/* Evaluation Metrics Section */}
      <Grid item xs={12} mt={4}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h6" fontWeight={500}>
            Evaluation Metrics of MQE Algorithm
            {selectedSubCategory?.name && ` on ${selectedSubCategory.name}`}
          </Typography>

          <Stack direction="row" gap={2}>
            <AnimateButton>
              <PrimaryButton
                title="Import Metrics"
                onClick={() => setOpenUploadDialog(true)}
              />
            </AnimateButton>

            <AnimateButton>
              <PrimaryButton
                title="Export Metrics"
                onClick={handleDownloadCSV}
              />
            </AnimateButton>
          </Stack>
        </Stack>
      </Grid>
      {/* Evaluation Metrics Table - auto-updates when data changes */}
      <Grid item xs={12}>
        <MaterialReactTable table={metricsTable} />
      </Grid>
      {/* Upload Dialog */}
      <Dialog
        open={openUploadDialog}
        onClose={() => setOpenUploadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography>Upload Metrics</Typography>
            <IconButton
              aria-label="close"
              onClick={() => setOpenUploadDialog(false)}
              sx={{
                color: theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <DropzoneContainer
              file={file}
              setFile={setFile}
              uploadProgress={uploadProgress}
              setUploadProgress={setUploadProgress}
              title="Drag and drop a CSV file here, or click the button below to select one"
              buttonName="Upload Metric CSV"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            width="100%"
            p={2}
          >
            <AnimateButton>
              <PrimaryButton
                title="Import"
                style={{
                  padding: '8px 20px',
                }}
                onClick={handleUploadCSV}
                disabled={!file}
              />
            </AnimateButton>
          </Box>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this metric? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="info"
            sx={{ color: 'white' }}
            onClick={handleDialogClose}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDialogConfirm}
            variant="contained"
            color="error"
            autoFocus
            sx={{ color: 'white' }}
            disabled={deleteMetric.isPending}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

// Query client with proper configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30 * 1000, // 30 seconds - data stays fresh
      cacheTime: 5 * 60 * 1000, // 5 minutes cache
    },
  },
});

const BackTestWithProviders = () => (
  <QueryClientProvider client={queryClient}>
    <MainBackTests />
  </QueryClientProvider>
);

export default BackTestWithProviders;
